import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Briefcase, MapPin, ArrowLeft, Sparkles } from 'lucide-react';
import { api } from '../../lib/api';
import { useUrlPrefix } from '../../lib/useUrlPrefix';
import { type Slot, type SlotsResponse, browserTz, formatSlotLocal } from '../../lib/slots';
import SlotPicker from '../../components/SlotPicker';
import { type MeetingType, MeetingTypePicker, offeredModes } from '../../lib/meetingType';

type Service = {
  id: string;
  slug: string;
  name: string;
  offers_in_person: number;
  offers_phone: number;
  offers_video: number;
};
type LocationWithServices = {
  id: string;
  name: string;
  slug: string;
  is_virtual: number;
  services: Service[];
};

type AnyConfirmResult = {
  start_time: string;
  staff_name: string;
  meeting_link: string | null;
  bookee_email: string | null;
  confirmation_message: string | null;
};

/**
 * "Anyone available" booking flow: merged availability across every staff
 * member who offers this (location, service), with the staff member auto-
 * assigned at confirm time. Mirrors BookPage's slot UI via the shared
 * SlotPicker; differs only in that there's no pre-chosen staff member.
 */
export default function AnyAvailableBook() {
  const { orgSlug, locSlug, svcSlug } = useParams();
  const prefix = useUrlPrefix();
  const tz = useMemo(browserTz, []);

  const [serviceName, setServiceName] = useState<string | null>(null);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [duration, setDuration] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [picked, setPicked] = useState<Slot | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState<AnyConfirmResult | null>(null);
  const [bookee, setBookee] = useState({ name: '', email: '', phone: '', notes: '' });
  const [modes, setModes] = useState<MeetingType[]>([]);
  const [meetingMode, setMeetingMode] = useState<MeetingType | null>(null);

  // Display names for the context pills + the service's offered meeting modes.
  useEffect(() => {
    if (!orgSlug || !locSlug || !svcSlug) return;
    api<LocationWithServices>(`/book/${orgSlug}/locations/${locSlug}`)
      .then((d) => {
        setLocationName(d.name);
        const svc = d.services.find((s) => s.slug === svcSlug);
        setServiceName(svc?.name ?? svcSlug);
        const m = svc ? offeredModes(svc) : [];
        setModes(m);
        setMeetingMode(m[0] ?? null);
      })
      .catch((e) => setErr((e as Error).message || 'load_failed'));
  }, [orgSlug, locSlug, svcSlug]);

  // Merged availability across all eligible staff.
  useEffect(() => {
    if (!orgSlug || !locSlug || !svcSlug) return;
    const now = new Date();
    const from = now.toISOString();
    const to = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString();
    api<SlotsResponse>(
      `/book/${orgSlug}/locations/${locSlug}/services/${svcSlug}/any-slots?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    )
      .then((r) => {
        setSlots(r.slots);
        setDuration(r.duration_minutes);
      })
      .catch((e) => setErr((e as Error).message || 'load_failed'));
  }, [orgSlug, locSlug, svcSlug]);

  async function submit() {
    if (!picked || !orgSlug || !locSlug || !svcSlug) return;
    if (!bookee.name.trim()) {
      setErr('name_required');
      return;
    }
    if (meetingMode === 'phone_call' && !bookee.phone.trim()) {
      setErr('phone_required');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const row = await api<AnyConfirmResult>(
        `/book/${orgSlug}/locations/${locSlug}/services/${svcSlug}/any-confirm`,
        {
          body: {
            start_time: picked.startUtc,
            timezone: tz,
            meeting_type: meetingMode ?? undefined,
            bookee: {
              name: bookee.name,
              email: bookee.email || null,
              phone: bookee.phone || null,
            },
            notes: bookee.notes || null,
          },
        },
      );
      setConfirmed(row);
    } catch (e) {
      setErr((e as Error).message || 'submit_failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (err === 'not_found' || err === 'not_bookable') {
    return <h1>This service isn&rsquo;t available to book right now.</h1>;
  }

  if (confirmed) {
    return (
      <div>
        <div className="card">
          <h1>You&apos;re booked with {confirmed.staff_name}</h1>
          <p>
            {formatSlotLocal(confirmed.start_time, tz)} — confirmation sent to{' '}
            <strong>{confirmed.bookee_email ?? 'your contact info'}</strong>.
          </p>
          {confirmed.meeting_link && (
            <p>
              Join link: <a href={confirmed.meeting_link}>{confirmed.meeting_link}</a>
            </p>
          )}
          <p className="muted" style={{ marginTop: '1.25rem', fontSize: '0.85rem' }}>
            Booking handled via Invitey.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="book-context">
        <Link
          to={`${prefix}/${orgSlug}/locations/${locSlug}/services/${svcSlug}`}
          className="btn ghost small"
        >
          <ArrowLeft size={12} /> Pick a specific person instead
        </Link>
      </div>

      <h1>
        <Sparkles size={18} style={{ verticalAlign: '-3px' }} /> Anyone available
      </h1>
      <div className="book-context">
        <span className="book-context-pill">
          <Briefcase size={12} /> {serviceName ?? svcSlug}
        </span>
        <span className="book-context-pill">
          <MapPin size={12} /> {locationName ?? locSlug}
        </span>
      </div>
      <p className="muted">
        Times shown in <strong>{tz}</strong>. Duration: {duration} min. We&rsquo;ll match you
        with a team member who&rsquo;s free for the time you pick.
      </p>

      {!slots && !err && <p className="muted">Looking up open times…</p>}
      {err && !slots && <p className="err">Error: {err}</p>}
      {slots && <SlotPicker slots={slots} tz={tz} picked={picked} onPick={setPicked} />}

      {picked && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Your info</h2>
          <p className="muted">Booking {formatSlotLocal(picked.startUtc, tz)}</p>
          {modes.length > 1 && (
            <label>
              <span>How would you like to meet?</span>
              <MeetingTypePicker
                value={meetingMode ?? modes[0]!}
                onChange={setMeetingMode}
                options={modes}
              />
            </label>
          )}
          <label>
            <span>Name *</span>
            <input value={bookee.name} onChange={(e) => setBookee({ ...bookee, name: e.target.value })} />
          </label>
          <div className="grid-2">
            <label>
              <span>Email</span>
              <input
                type="email"
                value={bookee.email}
                onChange={(e) => setBookee({ ...bookee, email: e.target.value })}
              />
            </label>
            <label>
              <span>Phone {meetingMode === 'phone_call' ? '*' : ''}</span>
              <input
                type="tel"
                value={bookee.phone}
                onChange={(e) => setBookee({ ...bookee, phone: e.target.value })}
              />
              {meetingMode === 'phone_call' && (
                <p className="field-help" style={{ marginBottom: 0 }}>
                  We&rsquo;ll call this number for your appointment.
                </p>
              )}
            </label>
          </div>
          <label>
            <span>Notes (optional)</span>
            <textarea
              rows={3}
              value={bookee.notes}
              onChange={(e) => setBookee({ ...bookee, notes: e.target.value })}
            />
          </label>
          {err && (
            <p className="err">
              {err === 'slot_taken'
                ? 'That time just filled up across the team — pick another.'
                : err === 'past_slot'
                  ? 'That time has already started — pick a future slot.'
                  : err === 'name_required'
                    ? 'Please enter your name.'
                    : err === 'phone_required'
                      ? 'A phone number is required for a phone-call booking.'
                      : `Error: ${err}`}
            </p>
          )}
          <button className="primary" onClick={submit} disabled={submitting}>
            {submitting ? 'Booking…' : 'Confirm booking'}
          </button>
        </div>
      )}
    </div>
  );
}
